import React from "react";
import { Link } from "react-router-dom";
import { Date, Ol, Ul, H2, Blockquote, Subtitle } from "./elements";

export const notes = {
    "automating-commit-messages-with-ai": {
        title: "Automating commit messages with AI",
        body: (
            <div>
                <p>
                    <Date>May 26, 2025</Date>
                </p>
                <p>
                    A task I have found AI to be consistently helpful with is
                    generating commit messages. Offloading this task to AI
                    conserves your energy for more important tasks and saves
                    time.
                </p>
                <p>
                    I automate this task by using{" "}
                    <a href="https://github.com/simonw/llm">LLM</a>. Follow the
                    instructions to get it{" "}
                    <a href="https://llm.datasette.io/en/stable/setup.html#installation">
                        installed
                    </a>
                    . Then, add a{" "}
                    <a href="https://llm.datasette.io/en/stable/templates.html">
                        template
                    </a>
                    . For the template name, use: "committer." For the template
                    prompt, use: "Describe the code change in a single sentence
                    that can be used for a git commit message. Make it 55
                    characters or less."
                </p>
                <p>Here is the command I used to create the template:</p>
                <p>
                    <pre>
                        llm --system 'Describe the code change in a single
                        sentence that can be used for a git commit message. Make
                        it 55 characters or less.' --model gpt-4o --save
                        committer
                    </pre>
                </p>
                <p>
                    Next, create a shell script called{" "}
                    <code>committer.zsh</code> like this:
                </p>
                <p>
                    <pre>
                        #!/usr/bin/env zsh
                        <br />
                        <br />
                        git diff | llm -t committer | pbcopy
                        <br />
                        git add .<br />
                        git commit -m "$(pbpaste)"
                    </pre>
                </p>
                <p>
                    Run the script with <code>./committer.zsh</code> when you
                    are ready to make a commit. It will generate a commit
                    message based on the code changes, add all the changes to
                    git, and commit them with the generated message.
                </p>
            </div>
        ),
    },
    "zen-of-python": {
        title: "The Zen of Python",
        body: (
            <div>
                <Subtitle>by Tim Peters</Subtitle>
                <p>
                    Beautiful is better than ugly.
                    <br />
                    Explicit is better than implicit.
                    <br />
                    Simple is better than complex.
                    <br />
                    Complex is better than complicated.
                    <br />
                    Flat is better than nested.
                    <br />
                    Sparse is better than dense.
                    <br />
                    Readability counts.
                    <br />
                    Special cases aren't special enough to break the rules.
                    <br />
                    Although practicality beats purity.
                    <br />
                    Errors should never pass silently.
                    <br />
                    Unless explicitly silenced.
                    <br />
                    In the face of ambiguity, refuse the temptation to guess.
                    <br />
                    There should be one-- and preferably only one --obvious way
                    to do it.
                    <br />
                    Although that way may not be obvious at first unless you're
                    Dutch.
                    <br />
                    Now is better than never.
                    <br />
                    Although never is often better than *right* now.
                    <br />
                    If the implementation is hard to explain, it's a bad idea.
                    <br />
                    If the implementation is easy to explain, it may be a good
                    idea.
                    <br />
                    Namespaces are one honking great idea -- let's do more of
                    those!
                </p>
                <p>
                    (
                    <a href="https://www.python.org/dev/peps/pep-0020/">
                        PEP 20
                    </a>
                    )
                </p>
            </div>
        ),
    },
    "test-driven-development": {
        title: "What is TDD?",
        body: (
            <div>
                <p>
                    <Date>January 1, 2025</Date>
                </p>
                <p>
                    I find test-driven development (TDD) to be a helpful
                    process, especially for fixing bugs and adding small
                    features.
                </p>
                <H2>TDD process</H2>
                <p>Here is the TDD process:</p>
                <Ul>
                    <li>
                        <b>Write a test:</b> The test should fail because the
                        program doesn't have that feature yet or because it
                        replicates an existing bug.
                    </li>
                    <li>
                        <b>Make a code change:</b> Write the simplest code
                        possible to make the test pass.
                    </li>
                    <li>
                        <b>Refactor:</b> Refactor the code to make it more
                        efficient or easier to read.
                    </li>
                </Ul>
                <H2>When to use TDD</H2>
                <p>
                    For bugs, TDD is invaluable. Writing a test that replicates
                    the exact reported issue ensures that the bug is truly fixed
                    when the test passes and stays fixed as long as the test
                    remains part of your suite.
                </p>
                <p>
                    For small features with clear requirements, following TDD
                    and writing tests first helps to ensure that the feature
                    works as expected.
                </p>
                <p>
                    Sometimes, it's possible to break a large feature into
                    smaller parts and use TDD for each part.
                </p>
                <H2>When not to use TDD</H2>
                <p>
                    Often, when building large new features, especially when the
                    requirements aren't yet clear, I don't strictly follow TDD.
                    I find I can write code faster without writing tests first
                    in these cases, and writing tests first for large features
                    can stifle creativity and experimentation. But, I do write
                    tests for new features before I consider them done, which
                    helps in refactoring, catching bugs, reducing the risk of
                    regressions, and documenting how the feature works.
                </p>
                <H2>AI and tests</H2>
                <p>
                    AI assistants like ChatGPT and Claude can be valuable tools
                    for writing tests. By providing the code you want to test
                    and requesting test suggestions, you can save time. But,
                    always review AI-generated tests carefully and make
                    adjustments to ensure they are accurate and align with your
                    project's requirements.
                </p>
                <H2>More information</H2>
                <p>
                    For more information on TDD, see Kent Beck's book{" "}
                    <a href="https://www.amazon.com/s?k=Test+Driven+Development+by+Kent+Beck">
                        Test Driven Development: By Example
                    </a>
                    . If you happen to be using my favorite web framework,
                    Django, there is some really good{" "}
                    <a href="https://docs.djangoproject.com/en/dev/topics/testing/">
                        documentation
                    </a>{" "}
                    available on writing tests.
                </p>
            </div>
        ),
    },
    "static-websites": {
        title: "Static websites with React and AWS",
        body: (
            <div>
                <p>
                    <Date>September 14, 2024</Date>
                </p>
                <p>
                    I've had some success with creating static websites using
                    React and AWS. I created this website with those
                    technologies, and I created PracticeJS (site no longer
                    exists), which made the front page of{" "}
                    <a href="https://news.ycombinator.com/item?id=20135047">
                        Hacker News
                    </a>
                    . On a high level, this is what works for me.
                </p>
                <p>
                    I use React and React Router to create a simple CMS that
                    uses a JavaScript object to store the data for the website.{" "}
                    <a href="https://github.com/duane9/static-website-demo">
                        Here
                    </a>{" "}
                    is an example of what the code looks like.
                </p>
                <p>
                    I use an Amazon S3 bucket to host the website, setting the
                    error document and index document to index.html so that it
                    works right with React Router. I set up CloudFront to use
                    the S3 bucket as its origin and set 404s to return as 200s.
                </p>
                <p>
                    I like to set up a custom SSL certificate with Certificate
                    Manager and CloudFront. Then point the domain to CloudFront
                    with Route 53.
                </p>
                <p>
                    I store the code for the website in GitHub and configure{" "}
                    <a href="https://github.com/marketplace/actions/aws-s3-github-action">
                        GitHub Actions
                    </a>{" "}
                    to update the S3 bucket every time I push a commit. After
                    everything is set up, I just need to update the JavaScript
                    object and push the changes to GitHub to update the website.
                </p>
                <p>
                    For more details about using S3 and CloudFront to host
                    static websites see this{" "}
                    <a href="https://docs.aws.amazon.com/whitepapers/latest/build-static-websites-aws/build-static-websites-aws.html">
                        whitepaper
                    </a>
                    . Basically, it's a bulletproof way to build a website.
                </p>
            </div>
        ),
    },
    "think-first-code-later": {
        title: "Think first, code later",
        body: (
            <div>
                <p>
                    I liked Jonathan McPherson's post about lessons he's learned
                    from{" "}
                    <a href="http://jmcphers.github.io/blog/dev-lessons/">
                        20 years of software development
                    </a>
                    . Here is my favorite tip:
                </p>
                <Blockquote>
                    <strong>Think first, code later.</strong> Just twenty
                    minutes sketching out the outline of what I'm doing helps me
                    think about what I'm building end-to-end. If I can't at
                    least walk through how it's going to work in my head, I
                    don't often start coding, because I'm liable to waste time
                    going down dead ends.
                </Blockquote>

                {/*<Blockquote>
                    <strong>Start coding as soon as you can.</strong> Some
                    people plan in a very detailed way before they start coding.
                    I'm of the opinion that this is often a waste of time. At a
                    sufficiently low level of detail, you may as well be coding.
                    Once you have a rough picture of the system in your head and
                    have the major technical decisions made, there is no
                    substitute for actually writing code. It's often easier to
                    let your code tell you what you need to get the system
                    working than it is to guess exhaustively ahead of time.
                </Blockquote>*/}
                <p>
                    He also has a helpful{" "}
                    <a href="http://jmcphers.github.io/blog/efficient-editing/">
                        introduction to Vim
                    </a>
                    .
                </p>
            </div>
        ),
    },
    "de-morgans-laws": {
        title: "De Morgan's laws",
        body: (
            <div>
                <p>
                    <code>
                        not (p and q) == not p or not q<br />
                        not (p or q) == not p and not q
                    </code>
                </p>
                <p>
                    (
                    <a href="https://en.wikipedia.org/wiki/De_Morgan%27s_laws">
                        wikipedia
                    </a>
                    )
                </p>
            </div>
        ),
    },
    django: {
        title: "Django notes",
        body: (
            <div>
                <Ul>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/">
                            Django documentation
                        </a>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/ref/models/">
                            Models
                        </a>
                        <br />
                        <code>from django.db import models</code>
                    </li>
                    <Ul>
                        <li>
                            <a href="https://docs.djangoproject.com/en/dev/ref/models/fields/">
                                Fields
                            </a>
                        </li>
                    </Ul>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/ref/templates/">
                            Templates
                        </a>
                    </li>
                    <Ul>
                        <li>
                            <a href="https://docs.djangoproject.com/en/dev/ref/templates/builtins/">
                                Template tags and filters
                            </a>
                        </li>
                    </Ul>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/http/views/">
                            Views
                        </a>
                    </li>
                    <Ul>
                        <li>
                            <a href="https://docs.djangoproject.com/en/dev/topics/http/shortcuts/">
                                HttpResponse
                            </a>
                            <br />
                            <code>from django.http import HttpResponse</code>
                        </li>
                        <li>
                            <a href="https://docs.djangoproject.com/en/dev/topics/http/shortcuts/">
                                redirect
                            </a>
                            <br />
                            <code>from django.shortcuts import redirect</code>
                        </li>
                        <li>
                            <a href="https://docs.djangoproject.com/en/dev/topics/class-based-views/">
                                Class-based views
                            </a>
                        </li>
                    </Ul>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/http/urls/">
                            URL dispatcher
                        </a>
                        <br />
                        <code>from django.urls import path, re_path</code>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/ref/forms/">
                            Forms
                        </a>
                        <br />
                        <code>from django import forms</code>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/cache/">
                            Cache
                        </a>
                        <br />
                        <code>from django.core.cache import cache</code>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/howto/custom-management-commands/">
                            Management commands
                        </a>
                        <br />
                        <code>
                            from django.core.management.base import BaseCommand
                        </code>
                        <br />
                        <code>
                            from django.core.management import call_command
                        </code>
                    </li>
                    <Ul>
                        <li>
                            Run a django-aware script
                            <br />
                            <code>
                                python manage.py shell &lt; your_script.py
                            </code>
                        </li>
                    </Ul>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/http/middleware/">
                            Middleware
                        </a>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/db/queries/">
                            Queries
                        </a>
                    </li>
                    <Ul>
                        <li>
                            <a href="https://docs.djangoproject.com/en/dev/topics/db/queries/#complex-lookups-with-q-objects">
                                Q objects
                            </a>
                            <br />
                            <code>from django.db.models import Q</code>
                        </li>
                    </Ul>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/ref/settings/">
                            Settings
                        </a>
                        <br />
                        <code>from django.conf import settings</code>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/testing/">
                            Testing
                        </a>
                        <br />
                        <code>from django.test import TestCase</code>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/auth/default/#user-objects">
                            Users
                        </a>
                        <br />
                        <code>from django.contrib.auth.models import User</code>
                        <p>Change password</p>
                        <pre>
                            user = User.objects.get(...)
                            <br />
                            user.set_password("new_password")
                            <br />
                            user.save()
                        </pre>
                    </li>
                    <li>
                        <a href="https://docs.djangoproject.com/en/dev/topics/db/sql/#executing-custom-sql-directly">
                            Executing SQL directly
                        </a>
                        <br />
                        <pre>
                            from django.db import connection
                            <br />
                            c = connection.cursor()
                            <br />
                            c.execute(...)
                            <br />
                            c.close()
                        </pre>
                    </li>
                </Ul>
                <H2>
                    <a href="https://www.django-rest-framework.org">
                        Django REST framework
                    </a>
                </H2>
                <Ul>
                    <li>
                        <a href="https://www.django-rest-framework.org/api-guide/serializers/">
                            Serializers
                        </a>
                        <br />
                        <code>from rest_framework import serializers</code>
                    </li>
                    <li>
                        <a href="https://www.django-rest-framework.org/api-guide/views/">
                            Views
                        </a>
                        <br />
                        <code>from rest_framework.views import APIView</code>
                        <br />
                        <code>
                            from rest_framework.decorators import api_view
                        </code>
                    </li>
                    <Ul>
                        <li>
                            <a href="https://www.django-rest-framework.org/api-guide/responses/">
                                Responses
                            </a>
                            <br />
                            <code>
                                from rest_framework.response import Response
                            </code>
                        </li>
                        <li>
                            <a href="https://www.django-rest-framework.org/api-guide/status-codes/">
                                Status codes
                            </a>
                            <br />
                            <code>from rest_framework import status</code>
                        </li>
                    </Ul>
                    <li>
                        <a href="https://www.django-rest-framework.org/api-guide/testing/">
                            Testing
                        </a>
                        <br />
                        <code>
                            from rest_framework.test import APIClient,
                            APITestCase
                        </code>
                    </li>
                </Ul>
            </div>
        ),
    },
    "þetta-reddast": {
        title: "Þetta reddast",
        body: (
            <div>
                <Blockquote>
                    Þetta reddast (pronounced thet-ta red-ust) ... means "it'll
                    all work out in the end" -- and if Iceland had an official
                    slogan, this would be it. The phrase near-perfectly sums up
                    the way Icelanders seem to approach life: with a laid-back,
                    easy-going attitude and a great sense of humour. (
                    <a href="https://www.bbc.com/travel/article/20180603-the-unexpected-philosophy-icelanders-live-by">
                        BBC
                    </a>
                    )
                </Blockquote>
                <p>
                    Also see:{" "}
                    <Link to={"/books/little-book-of-the-icelanders"}>
                        The Little Book of the Icelanders
                    </Link>
                    .
                </p>
            </div>
        ),
    },
    "galls-law": {
        title: "Gall's law",
        body: (
            <div>
                <Blockquote>
                    A complex system that works is invariably found to have
                    evolved from a simple system that worked. A complex system
                    designed from scratch never works and cannot be patched up
                    to make it work. You have to start over with a working
                    simple system. (
                    <a href="https://en.wikipedia.org/wiki/John_Gall_(author)#Gall.27s_law">
                        wikipedia
                    </a>
                    )
                </Blockquote>
            </div>
        ),
    },
    es6: {
        title: "ES6+ notes",
        body: (
            <Ul>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/async_function">
                        Async functions
                    </a>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions/">
                        Arrow functions
                    </a>
                    <br />
                    <code>{`const foo = (x, y) => { return x + y; };`}</code>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes">
                        Classes
                    </a>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/const">
                        const
                    </a>
                    <br />
                    <code>{`const foo = "foo";`}</code>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Default_parameters">
                        Default parameters
                    </a>
                    <br />
                    <code>{`const foo = (x, y = 1) => { return x + y; };`}</code>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/filter">
                        filter
                    </a>
                    <br />
                    <code>{`[1, 2, 3, 4, 5].filter(n => n < 3); // [1, 2]`}</code>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for...of">
                        for-of loop
                    </a>
                    <br />
                    <code>{`for (let item of items) {console.log(item)}`}</code>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import">
                        import
                    </a>{" "}
                    /{" "}
                    <a href="https://developer.mozilla.org/en-US/docs/web/javascript/reference/statements/export">
                        export
                    </a>
                    <pre>
                        {`
export default defaultExport;
import defaultExport from "module-name";
export function exportFunction(){...};
import { exportFunction } from "module-name";
            `}
                    </pre>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/let">
                        let
                    </a>
                    <br />
                    <code>{`let foo = "foo";`}</code>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise">
                        Promises
                    </a>
                    <pre>
                        {`
const promise1 = new Promise(function(resolve, reject) {
  setTimeout(function() {
    resolve('foo');
  }, 300);
});

promise1.then(function(value) {
  console.log(value); // foo
});
          `}
                    </pre>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/reduce">
                        reduce
                    </a>
                    <pre>
                        {`
const array1 = [1, 2, 3, 4];
const reducer = (accumulator, currentValue) => {
  return accumulator + currentValue;
}
console.log(array1.reduce(reducer)); // 10
          `}
                    </pre>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set">
                        Set
                    </a>
                    <pre>
                        {`
const set1 = new Set([1, 2, 3]);
console.log(set1.has(1)); // true
console.log(set1.has(2)); // true
console.log(set1.has(3)); // true

set1.add(4)
console.log(set1.has(4)); // true

set1.delete(4)
console.log(set1.has(4)); // false
console.log(set1.size); // 3
            `}
                    </pre>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax">
                        Spread
                    </a>
                    <pre>
                        {`
function sum(x, y, z) {
  return x + y + z;
}

const numbers = [1, 2, 3];

console.log(sum(...numbers)); // 6
          `}
                    </pre>
                </li>
                <li>
                    <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals">
                        Template literals (Template strings)
                    </a>
                </li>
                <pre>
                    &#96;string text&#96;
                    <br />
                    &#96;string text line 1<br />
                    &nbsp;string text line 2&#96;
                    <br />
                    &#96;string text &#36;&#123;expression&#125; string
                    text&#96;
                </pre>
            </Ul>
        ),
    },
    vim: {
        title: "Vim plugins",
        body: (
            <div>
                <p>
                    <Date>March 2, 2019</Date>
                </p>
                <p>
                    Vim is a powerful and customizable editor. With the right
                    plugins, it's better than any IDE, and it won't lock you
                    into a specific language. I use Vim to work with Python,
                    JavaScript, CSS, and HTML. Here are some of my favorite
                    plugins.
                </p>
                <Ul>
                    <li>
                        <a href="https://github.com/junegunn/fzf">fzf</a> - Fast
                        navigation through the files of a project.
                    </li>
                    <li>
                        <a href="https://github.com/yegappan/grep">grep</a> -
                        Use grep within Vim.
                    </li>
                    <li>
                        <a href="https://github.com/ervandew/supertab">
                            supertab
                        </a>{" "}
                        - Tab autocomplete. Use it with{" "}
                        <a href="https://github.com/davidhalter/jedi-vim">
                            jedi-vim
                        </a>
                        .
                    </li>
                    <li>
                        <a href="https://github.com/nvie/vim-flake8">
                            vim-flake8
                        </a>{" "}
                        - Python formatting.
                    </li>
                    <li>
                        <a href="https://github.com/prettier/vim-prettier">
                            vim-prettier
                        </a>{" "}
                        - JavaScript formatting.
                    </li>
                    <li>
                        <a href="https://github.com/tpope/vim-fugitive">
                            vim-fugitive
                        </a>{" "}
                        - Use Git within Vim.
                    </li>
                    <li>
                        <a href="https://github.com/airblade/vim-gitgutter">
                            vim-gitgutter
                        </a>{" "}
                        - See what lines have changed.
                    </li>
                    <li>
                        <a href="https://github.com/gregsexton/MatchTag">
                            MatchTag
                        </a>{" "}
                        - Highlights matching HTML tags.
                    </li>
                    <li>
                        <a href="https://github.com/ap/vim-css-color.git">
                            vim-css-color
                        </a>{" "}
                        - Preview colors in source code.
                    </li>
                    <li>
                        <a href="https://github.com/tpope/vim-commentary">
                            vim-commentary
                        </a>{" "}
                        - Comment stuff out.
                    </li>
                </Ul>
                <p>
                    Update:{" "}
                    <Link to={"/notes/why-i-switched-to-neovim"}>
                        I switched to Neovim
                    </Link>
                    , and I still use most of these plugins.
                </p>
            </div>
        ),
    },
    "why-i-switched-to-neovim": {
        title: "Why I switched to Neovim",
        body: (
            <div>
                <p>
                    <Date>May 11, 2019</Date>
                </p>
                <p>
                    I've been <Link to={"/notes/vim/"}>using Vim</Link> with
                    Tmux for about five years. It's a nice development
                    environment because it allows me to do almost everything
                    without using the mouse, which saves time.
                </p>
                <p>
                    I recently tried <a href="https://neovim.io/">Neovim</a>.
                    Neovim does everything Vim does. I can use all my favorite
                    Vim plugins and my vimrc file. Because it also has a
                    built-in terminal emulator, it can do everything I used Tmux
                    for, and it's all done with Vim commands. I don't have to
                    switch between Vim and Tmux commands. It's a better
                    experience, so I switched.
                </p>
                <p>
                    This is how to quickly get Neovim up and running on a Mac in
                    case you want to try it out.
                </p>
                <Ol>
                    <li>
                        Install Neovim:
                        <br />
                        <code>brew install neovim</code>
                    </li>
                    <li>
                        Copy your vimrc:
                        <br />
                        <code>cp ~/.vimrc ~/.config/nvim/init.vim</code>
                    </li>
                    <li>
                        Copy your plugins:
                        <br />
                        <code>
                            cp -r ~/.vim/pack/*/start/
                            ~/.local/share/nvim/site/pack/*/start/
                        </code>
                    </li>
                    <li>
                        Start up Neovim:
                        <br />
                        <code>nvim</code>
                    </li>
                </Ol>
                <p>
                    3/9/2024 Update: Vim now has a terminal emulator, so I am
                    using both Vim and Neovim.
                </p>
            </div>
        ),
    },
    "wisdom-from-kurt-vonnegut": {
        title: "Wisdom from Kurt Vonnegut",
        body: (
            <div>
                <Blockquote>
                    And how should we behave during an apocalypse? We should be
                    unusually kind to one another, certainly. But we should also
                    stop being so serious. Jokes help a lot. And get a dog if
                    you don't already have one. (
                    <a href="https://www.npr.org/2012/10/30/163722583/vonnegut-letters-hilarious-and-heartbreaking">
                        NPR
                    </a>
                    )
                </Blockquote>
                <p>
                    Also see:{" "}
                    <Link to={"/books/man-without-a-country"}>
                        A Man Without a Country
                    </Link>
                    .
                </p>
            </div>
        ),
    },
    "wisdom-from-bill-murray": {
        title: "Wisdom from Bill Murray",
        body: (
            <div>
                <Blockquote>
                    The more relaxed you are, the better you are at everything:
                    the better you are with your loved ones, the better you are
                    with your enemies, the better you are at your job, the
                    better you are with yourself. (
                    <a href="https://www.nytimes.com/2012/12/02/movies/bill-murray-star-of-hyde-park-on-hudson.html">
                        nytimes.com
                    </a>
                    )
                </Blockquote>
            </div>
        ),
    },
    "wisdom-from-michael-stipe": {
        title: "Wisdom from Michael Stipe",
        body: (
            <div>
                <Blockquote>
                    That's where God lives. You have to allow for the mistakes.
                    You have to not dismiss them, or instantly erase them, or
                    quantize them out. You have to allow for them. And that's
                    where you find real inspiration. That's where you find
                    instinct. (
                    <a href="https://www.newyorker.com/culture/the-new-yorker-interview/michael-stipe-wants-to-make-mistakes">
                        newyorker.com
                    </a>
                    )
                </Blockquote>
            </div>
        ),
    },
    //"wisdom-from-jerry-garcia": {
    //    title: "Wisdom from Jerry Garcia",
    //    body: (
    //        <div>
    //            <Blockquote>
    //                Material things matter almost not at all. No matter what I
    //                have around me, I treat it badly. Everything I've got has
    //                cigarette burns in it. I've never really been able to get
    //                attached to stuff. I have a few nice things. I've had nice
    //                things in the past and lost them mostly, or given them away
    //                or drifted apart from them. I have a hard time having
    //                material lust. There aren't that many things crying out to
    //                me on the material plane saying, "Have me, own me." I'm just
    //                not that sort of person. I never was. Experience is my
    //                material. If I'm attached to this plane, it's because of
    //                experience. That's the thing that keeps me here. (
    //                <a href="https://www.spin.com/2024/08/1987-grateful-dead-feature/">
    //                    spin.com
    //                </a>
    //                )
    //            </Blockquote>
    //        </div>
    //    ),
    //},
    //aws: {
    //    title: "AWS notes",
    //    body: (
    //        <div>
    //            <p>
    //                I passed the exam for{" "}
    //                <a href="https://www.credly.com/badges/280e029e-fc6a-4830-a38d-03436ff45459">
    //                    AWS Certified DevOps Engineer - Professional
    //                </a>
    //                . This is what I studied.
    //            </p>
    //            <Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codebuild/latest/userguide/welcome.html">
    //                        CodeBuild
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/codebuild/latest/userguide/build-spec-ref.html">
    //                            buildspec.yml
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codedeploy/latest/userguide/welcome.html">
    //                        CodeDeploy
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/codedeploy/latest/userguide/reference-appspec-file.html">
    //                            appspec.yml
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codepipeline/latest/userguide/welcome.html">
    //                        CodePipeline
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codeguru/latest/security-ug/what-is-codeguru-security.html">
    //                        CodeGuru
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codeguru/latest/profiler-ug/what-is-codeguru-profiler.html">
    //                        CodeGuru Profiler
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codeartifact/latest/ug/welcome.html">
    //                        CodeArtifact
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/imagebuilder/latest/userguide/what-is-image-builder.html">
    //                        EC2 Image Builder
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/Welcome.html">
    //                        CloudFormation
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/what-is-cfnstacksets.html">
    //                            StackSets
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/servicecatalog/latest/dg/what-is-service-catalog.html">
    //                        Service Catalog
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/elasticbeanstalk/latest/dg/Welcome.html">
    //                        Elastic Beanstalk
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/elasticbeanstalk/latest/dg/using-features.deploy-existing-version.html">
    //                            Elastic Beanstalk Deployments
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/elasticbeanstalk/latest/dg/ebextensions.html">
    //                            .ebextensions
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/serverless-application-model/latest/developerguide/what-is-sam.html">
    //                        Serverless Application Model (AWS SAM)
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/whitepapers/latest/introduction-devops-aws/aws-cdk.html">
    //                        AWS Cloud Development Kit (AWS CDK)
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/step-functions/latest/dg/welcome.html">
    //                        Step Functions (state machines)
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/systems-manager/latest/userguide/what-is-systems-manager.html">
    //                        Systems Manager (SSM)
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/systems-manager/latest/userguide/systems-manager-automation.html">
    //                            SSM Automation
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/systems-manager/latest/userguide/ssm-agent.html">
    //                            SSM Agent
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/systems-manager/latest/userguide/patch-manager.html">
    //                            SSM Patch Manager
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/systems-manager/latest/userguide/inventory-about.html">
    //                            SSM Inventory
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/systems-manager/latest/userguide/systems-manager-parameter-store.html">
    //                            Parameter Store
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/appconfig/latest/userguide/what-is-appconfig.html">
    //                        AppConfig
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/lambda/latest/dg/welcome.html">
    //                        Lambda
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/apigateway/latest/developerguide/welcome.html">
    //                        API Gateway
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AmazonECS/latest/developerguide/Welcome.html">
    //                        ECS
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonECS/latest/userguide/what-is-fargate.html">
    //                            Fargate
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonECS/latest/developerguide/clusters.html">
    //                            ECS clusters
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonECS/latest/developerguide/task_definitions.html">
    //                            ECS task definition
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AmazonECR/latest/userguide/what-is-ecr.html">
    //                        ECR
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/eks/latest/userguide/what-is-eks.html">
    //                        EKS
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/streams/latest/dev/introduction.html">
    //                        Kinesis Data Streams
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/firehose/latest/dev/what-is-this-service.html">
    //                        Data Firehose
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Introduction.html">
    //                        DynamoDB
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://aws.amazon.com/dynamodb/pricing/provisioned/">
    //                            RCU and WCU
    //                        </a>
    //                    </li>
    //                    <Ul>
    //                        <li>Write capacity units (WCU) examples</li>
    //                        <li>
    //                            Write 10 items per second, with item size 2KB
    //                        </li>
    //                        <li>10 * (2/1) = 20 WCUs</li>
    //                        <li>
    //                            Write 6 items per second, with item size 4.5KB
    //                        </li>
    //                        <li>6 * (5/1) = 30 WCUs</li>
    //                        <li>
    //                            Write 120 items per minute, with item size 2KB
    //                        </li>
    //                        <li>(120/60) * (2/1) = 4 WCUs</li>
    //                        <li>Read capacity units (RCU) examples.</li>
    //                        <li>
    //                            Read 10 strongly consistent reads per second,
    //                            with item size 4KB
    //                        </li>
    //                        <li>10 * (4/4) = 10 RCUs</li>
    //                        <li>
    //                            15 eventually consistent reads per second, with
    //                            item size 12KB
    //                        </li>
    //                        <li>(16/2) * (12/4) = 24 RCUs</li>
    //                        <li>
    //                            10 strongly consistent reads per second, with
    //                            item size 6KB
    //                        </li>
    //                        <li>10 * (8/4) = 20 RCUs</li>
    //                    </Ul>
    //                    <li>
    //                        <a href="https://aws.amazon.com/dynamodb/pricing/on-demand/">
    //                            On-demand
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://aws.amazon.com/blogs/database/choosing-the-right-dynamodb-partition-key/">
    //                            Partition key
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://aws.amazon.com/blogs/aws/local-secondary-indexes-for-amazon-dynamodb/">
    //                            Local secondary indexes
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/GSI.html">
    //                            Global secondary indexes (create anytime)
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/DAX.html">
    //                            DAX
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AmazonElastiCache/latest/dg/WhatIs.html">
    //                        ElastiCache
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/concepts.html">
    //                        EC2
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/autoscaling/ec2/userguide/what-is-amazon-ec2-auto-scaling.html">
    //                            EC2 Auto Scaling
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/autoscaling/ec2/userguide/AutoScalingGroup.html">
    //                            Auto Scaling groups
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://aws.amazon.com/autoscaling/">
    //                        Auto Scaling
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Welcome.html">
    //                        RDS
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/Welcome.html">
    //                        S3
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/bucket-encryption.html">
    //                            Encryption
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/dms/latest/userguide/Welcome.html">
    //                        DMS
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/SchemaConversionTool/latest/userguide/CHAP_Welcome.html">
    //                        SCT
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/WhatIsCloudWatch.html">
    //                        CloudWatch
    //                    </a>
    //                </li>
    //                <Ul>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/working_with_metrics.html">
    //                            CloudWatch metrics
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/AlarmThatSendsEmail.html">
    //                            CloudWatch alarms
    //                        </a>
    //                    </li>
    //                    <li>
    //                        <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/logs/WhatIsCloudWatchLogs.html">
    //                            CloudWatch Logs
    //                        </a>
    //                    </li>
    //                </Ul>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/athena/latest/ug/what-is.html">
    //                        Athena
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/awscloudtrail/latest/userguide/cloudtrail-user-guide.html">
    //                        CloudTrail
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/eventbridge/latest/userguide/eb-what-is.html">
    //                        EventBridge
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/health/latest/ug/what-is-aws-health.html">
    //                        Health Dashboard
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/AWSSimpleQueueService/latest/SQSDeveloperGuide/welcome.html">
    //                        SQS
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/xray/latest/devguide/aws-xray.html">
    //                        X-Ray
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/config/latest/developerguide/WhatIsConfig.html">
    //                        AWS Config
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/organizations/latest/userguide/orgs_manage_org.html">
    //                        AWS Organizations
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/controltower/latest/userguide/what-is-control-tower.html">
    //                        Control Tower
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/singlesignon/latest/userguide/what-is.html">
    //                        IAM Identity Center
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/waf/latest/developerguide/waf-chapter.html">
    //                        AWS WAF
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/waf/latest/developerguide/fms-chapter.html">
    //                        Firewall Manager
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/guardduty/latest/ug/what-is-guardduty.html">
    //                        GuardDuty
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/inspector/latest/user/what-is-inspector.html">
    //                        Inspector
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://aws.amazon.com/premiumsupport/technology/trusted-advisor/">
    //                        Trusted Advisor
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/secretsmanager/latest/userguide/intro.html">
    //                        Secrets Manager
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/quicksight/latest/user/welcome.html">
    //                        QuickSight
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/glue/latest/dg/what-is-glue.html">
    //                        Glue
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/kms/latest/developerguide/overview.html">
    //                        KMS
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/macie/latest/user/what-is-macie.html">
    //                        Macie
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/opensearch-service/latest/developerguide/what-is.html">
    //                        OpenSearch Service (formerly Elasticsearch Service -
    //                        Amazon ES)
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/opsworks/latest/userguide/welcome.html">
    //                        OpsWorks
    //                    </a>
    //                </li>
    //                {/* updated to this point
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/amplify/latest/userguide/welcome.html">
    //                        Amplify
    //                    </a>
    //                </li>
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/codecommit/latest/userguide/welcome.html">
    //                        CodeCommit
    //                    </a>
    //                </li> */}
    //                <li>
    //                    <a href="https://docs.aws.amazon.com/index.html">
    //                        More documentation
    //                    </a>
    //                </li>
    //            </Ul>
    //        </div>
    //    ),
    //},
};
